import * as Api from '@ViewModels';
import { UserSessionContext } from '@ViewModels';
import Observability from '@launchdarkly/observability';
import SessionReplay, { LDRecord } from '@launchdarkly/session-replay';
import { initialize, LDClient } from 'launchdarkly-js-client-sdk';
import { autorun, IReactionDisposer } from 'mobx';
import { type IImpersonationInfo, IMPERSONATION_INFO_KEY } from '../web/containers/CustomerSessionStart/impersonation';
import { AppState, IAppState } from './AppState';
import { clearLaunchDarklyStorage } from './LaunchDarklyStorage';
import { getDisplayName } from './NameUtils';

export const LAUNCHDARKLY_SESSION_REPLAY_BASE_URL = 'https://app.launchdarkly.com/projects/default/sessions';

const DEFAULT_ENVIRONMENT = 'test';
const SESSION_REPLAY_QUERY_PARAMS = {
	QUERY: 'query',
	PAGE: 'page',
	DEFAULT_PAGE: '1',
	RELATIVE_TIME: 'relativeTime',
	DEFAULT_RELATIVE_TIME: 'last_30_days',
	SELECTED_ENV: 'selected-env',
} as const;

const SESSION_PROPERTY_KEYS = {
	ENVIRONMENT: 'environment',
	USER_NAME: 'userName',
	USER_ID: 'userId',
	ACCOUNT_ID: 'accountId',
	ACCOUNT_NAME: 'accountName',
	USER_ROLE: 'userRole',
	IS_IMPERSONATED: 'isImpersonated',
	IMPERSONATED_BY_USER_ID: 'impersonatedByUserId',
	IMPERSONATED_BY_USER_NAME: 'impersonatedByUserName',
} as const;

/** How often to check if highlight.run has rotated the session (4-hour max). */
const SESSION_MONITOR_INTERVAL_MS = 60_000;

/**
 * Get session replay URL for attaching to Bugsnag error reports
 */
export async function getSessionReplayUrl(environment?: string): Promise<string | null> {
	try {
		const session = LDRecord.getSession();
		if (!session) return null;

		const sessionId = session.sessionSecureID;
		const env = environment || DEFAULT_ENVIRONMENT;

		const baseUrl = `${LAUNCHDARKLY_SESSION_REPLAY_BASE_URL}/${sessionId}`;
		const params = new URLSearchParams({
			[SESSION_REPLAY_QUERY_PARAMS.QUERY]: `secure_id=${sessionId}`,
			[SESSION_REPLAY_QUERY_PARAMS.PAGE]: SESSION_REPLAY_QUERY_PARAMS.DEFAULT_PAGE,
			[SESSION_REPLAY_QUERY_PARAMS.RELATIVE_TIME]: SESSION_REPLAY_QUERY_PARAMS.DEFAULT_RELATIVE_TIME,
			env,
			[SESSION_REPLAY_QUERY_PARAMS.SELECTED_ENV]: env,
		});

		return `${baseUrl}?${params.toString()}`;
	} catch {
		return null;
	}
}

/**
 * All errors in this class are silently caught — session replay failures should never break app functionality.
 */
// eslint-disable-next-line import/no-default-export
export default class SessionReplayLogger implements Api.ILogger<Api.ILogEvent> {
	private onAuthStateChangeDisposer: IReactionDisposer | null = null;
	private ldClient: LDClient | null = null;
	private previousUserId: string | null = null;
	private recordingStarted = false;
	private releaseStage: 'development' | 'staging' | 'production' | 'test';
	private sessionReplayClientId: string;
	private appState: IAppState;
	private lastKnownSessionId: string | null = null;
	private sessionMonitorInterval: ReturnType<typeof setInterval> | null = null;

	constructor(
		releaseStage: 'development' | 'staging' | 'production' | 'test' = 'production',
		sessionReplayClientId: string,
		appSt?: IAppState
	) {
		this.releaseStage = releaseStage;
		this.sessionReplayClientId = sessionReplayClientId;
		this.appState = appSt ?? AppState;
		this.onAuthStateChangeDisposer = null;
		this.ldClient = null;
		this.previousUserId = null;
		this.recordingStarted = false;

		// React to auth state changes using MobX autorun
		this.onAuthStateChangeDisposer = autorun(() => {
			this.handleAuthStateChange().catch(() => {
				// Silent catch — see class JSDoc
			});
		});
	}

	private async handleAuthStateChange(): Promise<void> {
		const { environment, userSession } = this.appState;

		// Only proceed if we're in web app and have necessary config
		if (!environment || environment.appType !== 'web' || !this.sessionReplayClientId) {
			return;
		}

		// Never record admin sessions
		const isAdminPage =
			this.appState.environment?.appType === 'admin' || window.location.pathname.includes('admin.html');

		const isAuthenticated = userSession?.isAuthenticated ?? false;
		const currentUserId = isAuthenticated ? userSession?.user?.id : null;

		// Detect user transitions and first-time initialization
		const isFirstInitialization = this.previousUserId === null && currentUserId !== null;
		const isUserTransition = this.previousUserId !== null && this.previousUserId !== currentUserId;

		// Coffee accounts require the realtime coaching toggle to be enabled
		const isCoffeeAccount = userSession?.account?.isLevitateSalesCoffeeAccount ?? false;
		const coffeeCoachingDisabled =
			isCoffeeAccount && !(userSession?.user?.userPreferences?.coffeePreferences?.realtimeCoachingEnabled ?? false);

		// Check if session replay should be disabled (HIPAA compliance, opt-out, admin pages, coffee without coaching)
		const shouldDisableRecording =
			!isAuthenticated || isAdminPage || userSession?.sessionReplayOptOut === true || coffeeCoachingDisabled;

		if (shouldDisableRecording) {
			// User logged out, opted out, or on admin page - stop and clean up completely
			if (this.ldClient) {
				try {
					this.stopSessionMonitor();
					LDRecord.stop();
					await this.ldClient.close();
					this.ldClient = null;
					this.previousUserId = null;
					this.recordingStarted = false;
					// Clear localStorage to prevent session carryover when a different user logs in
					clearLaunchDarklyStorage();
				} catch {
					// Silent catch — see class JSDoc
				}
			}
			return;
		}

		if (!this.ldClient) {
			await this.initializeClient(userSession, false);
		} else if (isUserTransition) {
			// User switched (e.g., impersonation start/end) - reinitialize
			try {
				this.stopSessionMonitor();
				LDRecord.stop();
				await this.ldClient.close();
				this.ldClient = null;
				this.recordingStarted = false;

				// Clear localStorage on ALL user transitions to prevent session correlation
				clearLaunchDarklyStorage();

				await this.initializeClient(userSession, true);
			} catch {
				// Silent catch — see class JSDoc
			}
		} else if (!isFirstInitialization) {
			// Same user, subsequent autorun trigger — resume existing session
			await this.resumeSession(userSession);
		}

		this.previousUserId = currentUserId;
	}

	private async initializeClient(userSession: UserSessionContext, forceNewSession: boolean): Promise<void> {
		try {
			const userId = userSession.user?.id;
			if (!userId) {
				return;
			}

			const userName = getDisplayName(userSession.user, false);
			const accountName = userSession.account?.companyName || 'Unknown Account';
			const displayName = `${userName} - ${accountName}`;

			const sessionReplay = new SessionReplay({
				privacySetting: 'none', // HIPAA accounts are filtered out by shouldDisableRecording above
				environment: this.releaseStage,
				tracingOrigins: false, // Disable trace billing
				contextFriendlyName: () => displayName,
				inlineStylesheet: false, // Don't serialize inline <style> tags - reduces payload size (default is true for localhost)
				manualStart: true, // Prevent SDK from auto-calling start() — we call LDRecord.start() explicitly
				inlineImages: false,
			});

			const userContext = {
				kind: 'user',
				key: userId,
				name: displayName,
			};

			this.ldClient = initialize(this.sessionReplayClientId, userContext, {
				sendEvents: false, // Disable MAU billing (we only use session replay)
				diagnosticOptOut: true, // Disable diagnostic events
				plugins: [
					sessionReplay,
					new Observability({
						networkRecording: {
							enabled: true,
							recordHeadersAndBody: true,
						},
						disableConsoleRecording: true,
						environment: this.releaseStage,
					}),
				],
			});

			await this.ldClient.waitUntilReady();

			if (!this.recordingStarted) {
				await LDRecord.start(forceNewSession ? { forceNew: true } : undefined);
				this.recordingStarted = true;
			}

			await this.addSessionProperties(userSession);
			this.startSessionMonitor(userSession);
		} catch {
			// Silent catch — see class JSDoc
		}
	}

	private async resumeSession(userSession: UserSessionContext): Promise<void> {
		if (!this.ldClient) {
			return;
		}

		try {
			await this.ldClient.waitUntilReady();

			if (!this.recordingStarted) {
				await LDRecord.start();
				this.recordingStarted = true;
			}

			await this.addSessionProperties(userSession);
			this.startSessionMonitor(userSession);
		} catch {
			// Silent catch — see class JSDoc
		}
	}

	private getImpersonationInfo(): IImpersonationInfo | null {
		try {
			const impersonationInfoJson = sessionStorage.getItem(IMPERSONATION_INFO_KEY);
			if (!impersonationInfoJson) return null;

			const parsed = JSON.parse(impersonationInfoJson);

			// Validate structure before casting
			if (typeof parsed?.isImpersonated === 'boolean') {
				const impersonationInfo = parsed as IImpersonationInfo;
				return impersonationInfo.isImpersonated ? impersonationInfo : null;
			}
			return null;
		} catch {
			// Silent catch — see class JSDoc
			return null;
		}
	}

	private async addSessionProperties(userSession: UserSessionContext): Promise<void> {
		try {
			const baseProperties: Record<string, string> = {
				[SESSION_PROPERTY_KEYS.ENVIRONMENT]: this.releaseStage,
				[SESSION_PROPERTY_KEYS.USER_NAME]: getDisplayName(userSession.user, false),
				[SESSION_PROPERTY_KEYS.USER_ID]: userSession.user?.id || 'unknown',
				[SESSION_PROPERTY_KEYS.ACCOUNT_ID]: userSession.account?.id || 'unknown',
				[SESSION_PROPERTY_KEYS.ACCOUNT_NAME]: userSession.account?.companyName || 'unknown',
				[SESSION_PROPERTY_KEYS.USER_ROLE]: userSession.user?.role || 'unknown',
			};

			// Add impersonation metadata if this is a "Login as" session
			const impersonationInfo = this.getImpersonationInfo();
			if (impersonationInfo) {
				baseProperties[SESSION_PROPERTY_KEYS.IS_IMPERSONATED] = 'true';
				if (impersonationInfo.impersonatedByUserId) {
					baseProperties[SESSION_PROPERTY_KEYS.IMPERSONATED_BY_USER_ID] = impersonationInfo.impersonatedByUserId;
				}
				if (impersonationInfo.impersonatedByUserName) {
					baseProperties[SESSION_PROPERTY_KEYS.IMPERSONATED_BY_USER_NAME] = impersonationInfo.impersonatedByUserName;
				}
			}

			LDRecord.addSessionProperties(baseProperties);
		} catch {
			// Silent catch — see class JSDoc
		}
	}

	/**
	 * Detects when highlight.run internally rotates the session (e.g. 4-hour max)
	 * and re-applies custom properties to the new session.
	 */
	private checkForSessionChange(userSession: UserSessionContext): void {
		try {
			const session = LDRecord.getSession();
			// getSession() can briefly return null during rotation — skip this tick
			if (!session) return;

			const currentId = session.sessionSecureID;
			if (currentId && currentId !== this.lastKnownSessionId) {
				this.lastKnownSessionId = currentId;
				this.addSessionProperties(userSession).catch(() => {
					// Silent catch — see class JSDoc
				});
			}
		} catch {
			// Silent catch — see class JSDoc
		}
	}

	private startSessionMonitor(userSession: UserSessionContext): void {
		this.stopSessionMonitor();

		// Capture initial session ID
		try {
			const session = LDRecord.getSession();
			this.lastKnownSessionId = session?.sessionSecureID ?? null;
		} catch {
			// Silent catch — see class JSDoc
		}

		this.sessionMonitorInterval = setInterval(() => {
			this.checkForSessionChange(userSession);
		}, SESSION_MONITOR_INTERVAL_MS);
	}

	private stopSessionMonitor(): void {
		if (this.sessionMonitorInterval) {
			clearInterval(this.sessionMonitorInterval);
			this.sessionMonitorInterval = null;
		}
		this.lastKnownSessionId = null;
	}

	public flush = () => {
		return Promise.resolve();
	};

	public getLogFormatter = () => {
		return null as Api.ILogFormatter<Api.ILogEvent>;
	};

	public getName(): string {
		return 'LaunchDarkly Session Replay';
	}

	public log(_logEvent: Api.ILogEvent): Promise<any> {
		return Promise.resolve();
	}

	public dispose = async () => {
		this.stopSessionMonitor();

		if (this.onAuthStateChangeDisposer) {
			this.onAuthStateChangeDisposer();
			this.onAuthStateChangeDisposer = null;
		}

		if (this.ldClient) {
			try {
				LDRecord.stop();
				await this.ldClient.close();
				this.ldClient = null;
				this.recordingStarted = false;
			} catch {
				// Silent catch — see class JSDoc
			}
		}
	};
}
